
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { useProAccess } from './useProAccess';
import { encryptAndSaveSensitiveFields, extractSensitiveFields, isEncrypted } from '@/lib/encryption';

export interface Bateau {
  id: string;
  nom: string;
  nom_constructeur: string;
  nom_modele: string;
  annee: number;
  annee_construction?: number;
  annee_achat?: number;
  longueur: number;
  largeur: number;
  tirant_eau?: number;
  couleur?: string;
  moteur: string;
  heures_moteur?: number;
  capacite_carburant: number;
  capacite_eau: number;
  capacite_eaux_usees?: number;
  numero_hin: string | null;
  immatriculation: string | null;
  mmsi: string | null;
  port_attache: string | null;
  pavillon: string | null;
  photo_url: string | null;
  note: string | null;
  par_defaut: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
}

export const useBateaux = () => {
  const [bateaux, setBateaux] = useState<Bateau[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadSucceeded, setLoadSucceeded] = useState(false);
  const { user } = useAuth();
  const { isProAccount } = useProAccess();

  const fetchBateaux = async (attempt = 0): Promise<void> => {
    if (!user) {
      setBateaux([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      console.log('Fetching bateaux for user:', user.id);
      const { data, error } = await supabase
        .from('bateaux')
        .select('*')
        .eq('user_id', user.id)
        .order('par_defaut', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erreur lors du chargement des bateaux:', error);
        // Retry une fois sur erreur réseau transitoire avant d'abandonner
        if (attempt < 1) {
          setLoading(false);
          await new Promise(r => setTimeout(r, 600));
          return fetchBateaux(attempt + 1);
        }
        toast({
          title: "Erreur",
          description: "Impossible de charger vos bateaux.",
          variant: "destructive",
        });
        // Ne pas écraser les bateaux déjà chargés
      } else {
        console.log('Bateaux loaded:', data?.length, 'boats');
        let mappedData = (data || []).map(boat => ({
          ...boat,
          numero_hin: boat.numero_hin ?? null,
          immatriculation: boat.immatriculation ?? null,
          mmsi: boat.mmsi ?? null,
          heures_moteur: boat.heures_moteur || 0
        })) as Bateau[];

        // Déchiffrer tous les champs sensibles côté serveur en un seul appel
        const hasEncrypted = (data || []).some(b =>
          isEncrypted(b.immatriculation) ||
          isEncrypted(b.mmsi) ||
          isEncrypted(b.numero_hin)
        );
        if (hasEncrypted) {
          try {
            const { data: decRes, error: decErr } = await supabase.functions.invoke('sensitive-data', {
              body: { action: 'decrypt_all_user_boats', data: {} },
            });
            if (!decErr && decRes?.data) {
              const map = decRes.data as Record<string, { immatriculation: string|null; mmsi: string|null; numero_hin: string|null }>;
              mappedData = mappedData.map(b => {
                const d = map[b.id];
                if (!d) return b;
                return {
                  ...b,
                  immatriculation: isEncrypted(d.immatriculation) ? null : d.immatriculation ?? b.immatriculation ?? null,
                  mmsi: isEncrypted(d.mmsi) ? null : d.mmsi ?? b.mmsi ?? null,
                  numero_hin: isEncrypted(d.numero_hin) ? null : d.numero_hin ?? b.numero_hin ?? null,
                };
              });
            } else if (decErr) {
              console.error('[useBateaux] decrypt error:', decErr);
            }
          } catch (e) {
            console.error('[useBateaux] decrypt exception:', e);
          }
        }
        setBateaux(mappedData);
        setLoadSucceeded(true);
      }
    } catch (err) {
      console.error('Erreur:', err);
      if (attempt < 1) {
        setLoading(false);
        await new Promise(r => setTimeout(r, 600));
        return fetchBateaux(attempt + 1);
      }
      // Ne pas écraser les bateaux déjà chargés en cas d'erreur réseau
    } finally {
      setLoading(false);
    }
  };

  const getBateauParDefaut = () => {
    return bateaux.find(bateau => bateau.par_defaut) || bateaux[0] || null;
  };

  const definirBateauParDefaut = async (bateauId: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      // D'abord, désactiver par_defaut pour tous les bateaux de l'utilisateur
      const { error: resetError } = await supabase
        .from('bateaux')
        .update({ par_defaut: false })
        .eq('user_id', user.id);

      if (resetError) {
        console.error('Erreur lors de la réinitialisation:', resetError);
        return { error: resetError };
      }

      // Ensuite, activer par_defaut pour le bateau sélectionné
      const { error } = await supabase
        .from('bateaux')
        .update({ par_defaut: true })
        .eq('id', bateauId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erreur lors de la définition du bateau par défaut:', error);
        toast({
          title: "Erreur",
          description: "Impossible de définir ce bateau par défaut.",
          variant: "destructive",
        });
        return { error };
      } else {
        // Rafraîchir les données
        await fetchBateaux();
        toast({
          title: "Succès",
          description: "Bateau défini par défaut !",
        });
        return { success: true };
      }
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const ajouterBateau = async (bateauData: Omit<Bateau, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    // Vérifier la limite de bateaux selon l'abonnement
    const maxBateaux = isProAccount ? 2 : 1;
    if (bateaux.length >= maxBateaux) {
      toast({
        title: "Limite atteinte",
        description: isProAccount
          ? "Les comptes Premium sont limités à 2 navires."
          : "Les comptes gratuits sont limités à 1 navire. Passez à l'abonnement Premium pour 2 navires.",
        variant: "destructive",
      });
      return { error: 'Limite de navires atteinte' };
    }

    try {
      // Séparer les champs sensibles des autres champs
      const { sensitiveFields, otherFields } = extractSensitiveFields(bateauData);

      const { data, error } = await supabase
        .from('bateaux')
        .insert([{ ...otherFields, user_id: user.id, numero_hin: null, mmsi: null, immatriculation: null } as any])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de l\'ajout du bateau:', error);
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter le bateau.",
          variant: "destructive",
        });
        return { error };
      }

      // Chiffrer et sauvegarder les champs sensibles via Edge Function
      if (data && Object.values(sensitiveFields).some(v => v)) {
        await encryptAndSaveSensitiveFields(data.id, sensitiveFields);
      }

      await fetchBateaux();
      toast({
        title: "Succès",
        description: "Bateau ajouté avec succès !",
      });
      return { data };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierBateau = async (id: string, bateauData: Partial<Omit<Bateau, 'id' | 'created_at' | 'updated_at' | 'user_id'>>) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      console.log('Modifying boat:', id);
      
      // Séparer les champs sensibles
      const { sensitiveFields, otherFields } = extractSensitiveFields(bateauData);

      // Mettre à jour les champs non-sensibles directement
      const { data, error } = await supabase
        .from('bateaux')
        .update(otherFields)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la modification du bateau:', error);
        toast({
          title: "Erreur",
          description: "Impossible de modifier le bateau.",
          variant: "destructive",
        });
        return { error };
      }

      // Chiffrer et sauvegarder les champs sensibles via Edge Function
      if (Object.keys(sensitiveFields).length > 0) {
        await encryptAndSaveSensitiveFields(id, sensitiveFields);
      }

      console.log('Boat updated successfully:', id);
      await fetchBateaux();
      toast({
        title: "Succès",
        description: "Bateau modifié avec succès !",
      });
      return { data };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const supprimerBateau = async (id: string) => {
    try {
      const { error } = await supabase
        .from('bateaux')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer le bateau.",
          variant: "destructive",
        });
      } else {
        // Rafraîchir les données au lieu de juste filtrer l'état local
        await fetchBateaux();
        toast({
          title: "Succès",
          description: "Bateau supprimé avec succès !",
        });
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const uploadPhoto = async (file: File, bateauId?: string) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${bateauId || Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('boat-photos')
        .upload(fileName, file, {
          upsert: true
        });

      if (uploadError) {
        console.error('Erreur upload:', uploadError);
        toast({
          title: "Erreur",
          description: "Impossible de télécharger la photo.",
          variant: "destructive",
        });
        return { error: uploadError };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('boat-photos')
        .getPublicUrl(fileName);

      return { data: publicUrl };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  useEffect(() => {
    fetchBateaux();
  }, [user]);

  return {
    bateaux,
    loading,
    loadSucceeded,
    ajouterBateau,
    modifierBateau,
    supprimerBateau,
    uploadPhoto,
    getBateauParDefaut,
    definirBateauParDefaut,
    refetch: fetchBateaux
  };
};
